import { useCallback, useEffect } from 'react';
import { nanoid } from 'nanoid';

import { useNotificationStore } from 'store/notifications';
import { useTranslation } from 'src/hooks/translation';

import Toast from './Toast';

const useToast = (delay = 2000, position?: ToastPosition) => {
  const t = useTranslation();
  const { data: toasts, mutate: setToasts } = useNotificationStore();

  const addToast = useCallback(
    async (toast: IToast) => {
      await setToasts([...(toasts ?? []), { ...toast, id: nanoid() }], false);
    },
    [setToasts, toasts],
  );

  const showError = async (error: ResponseError) => {
    await addToast({
      type: 'error',
      text:
        (error.error as RegularResponseError)?._error?.message ||
        (error.error as NonFieldResponseError)?.non_field_error ||
        t.common.error,
    });
  };

  const showErrorDirectly = async (text: string, title?: string) => {
    await addToast({ type: 'error', title, text });
  };

  const showSuccessNotification = useCallback(
    async (text: string, title?: string) => {
      await addToast({ type: 'success', title, text });
    },
    [addToast],
  );

  const removeToast = useCallback(
    (id?: string) => {
      setToasts(
        toasts?.filter((t) => t.id !== id),
        false,
      );
    },
    [toasts, setToasts],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (toasts?.length) {
        removeToast(toasts[0].id);
      }
    }, delay);

    return () => {
      clearInterval(interval);
    };
  }, [toasts, removeToast, delay]);

  const renderToasts = useCallback(() => {
    return <Toast toastList={toasts || []} removeToast={removeToast} position={position} />;
  }, [toasts, removeToast, position]);

  return { addToast, showError, showErrorDirectly, showSuccessNotification, renderToasts };
};

export default useToast;
